import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FooterSection } from '../components/landing';
import GoogleAuthButton from '../components/GoogleAuthButton';
import { useSEO } from '../hooks/useSEO';
import { trackClick } from '../utils/analytics';
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  FileText,
  Gauge,
  ListChecks,
  Shield,
  Sparkles,
  Target,
  X,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

type AiReport = {
  executive_summary?: string;
  key_themes?: string[];
  action_items?: string[];
  sentiment_label?: string | null;
  sentiment_score?: number | null;
};

type GenerateResponse = {
  report?: AiReport;
  session_token?: string;
  message?: string;
};

type AnalysisStep = {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'complete';
};

const templates = [
  {
    slug: 'start-stop-continue',
    name: 'Start / Stop / Continue',
    desc: 'Identify what to keep doing, drop, and try next sprint.',
    emoji: '🔄',
  },
  {
    slug: 'mad-sad-glad',
    name: 'Mad / Sad / Glad',
    desc: 'Surface team emotions and energy to improve morale.',
    emoji: '😊',
  },
  {
    slug: '4ls',
    name: '4Ls Retrospective',
    desc: 'Liked, Learned, Lacked, and Longed For — a complete picture.',
    emoji: '🔍',
  },
  {
    slug: 'sailboat',
    name: 'Sailboat',
    desc: 'Visualize what drives you forward and what holds you back.',
    emoji: '⛵',
  },
];

const features = [
  { icon: '🗂', bg: '#ffe9d1', title: '12+ retro templates', body: 'Start Stop Continue, 4Ls, Sailboat and more — pick a format and go, or build your own.' },
  { icon: '⚡', bg: '#e4f4ef', title: 'Real‑time collaboration', body: 'Everyone writes, votes and groups together. No refresh, no "can you see my cursor?".' },
  { icon: '🎭', bg: '#ece7ff', title: 'Anonymous voting', body: 'Honest feedback without the awkwardness. Reveal cards together to remove bias.' },
  { icon: '✅', bg: '#fdeef1', title: 'Action tracking', body: 'Turn any card into an action with an owner and due date — and revisit it next retro.' },
  { icon: '✦', bg: '#ffe9d1', title: 'AI summaries', body: 'Themes, trends and a ready‑to‑share summary drafted the moment your retro ends.' },
  { icon: '🌍', bg: '#e4f4ef', title: 'Async‑friendly', body: 'Team across time zones? Collect input before the meeting and keep the live part short.' },
];

const faqs = [
  { q: 'What is Retrosive?', a: 'Retrosive is an online retrospective tool for agile teams — run structured retros with real‑time collaboration, anonymous feedback, voting and action tracking, plus AI‑drafted summaries.' },
  { q: 'Is it really free to start?', a: 'Yes. You can run retros for free with no credit card required. Paid plans add unlimited history, AI features and integrations.' },
  { q: 'How does the AI summary work?', a: 'When your retro ends, Retrosive groups cards into themes, highlights recurring issues across sprints, and drafts a summary with suggested actions. You review and share it — nothing is sent without you.' },
  { q: 'Can remote and async teams use it?', a: 'Absolutely — team members can add cards before the meeting, and the live session works great over any video call.' },
  { q: 'Is my data secure?', a: 'Yes — boards are private to your team, anonymous input stays anonymous, and data is encrypted in transit and at rest.' },
];

const HomePage: React.FC = () => {
  useSEO({
    title: 'Retrosive | Team Retrospective Tool',
    description:
      'Run retrospectives your team actually looks forward to. Real-time collaboration, action tracking, and smart summaries — free forever.',
    canonical: '/',
  });

  const generatorRef = useRef<HTMLDivElement | null>(null);

  const [notes, setNotes] = useState('');
  const [pageState, setPageState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [report, setReport] = useState<AiReport | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [analysisSteps, setAnalysisSteps] = useState<AnalysisStep[]>([]);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isUsingDemoData, setIsUsingDemoData] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const sampleNotes = useMemo(
    () =>
      `CI/CD pipeline broke 3 times this sprint - config drift between staging and prod environments causing deployment failures. Frontend team blocked for 2 days waiting on updated API documentation from backend. Sprint velocity dropped 18% due to emergency hotfixes on the payment module.

Positives: Design system adoption hit 85%, new component library reducing dev time by ~30%. Team morale improved after introducing async standups.

Action needed: Automate API docs generation, implement infrastructure-as-code for environment parity, schedule cross-team sync for Q2 roadmap alignment.`,
    [],
  );

  const mockDemoReport: AiReport = useMemo(
    () => ({
      executive_summary:
        'This sprint experienced notable challenges with deployment stability due to configuration drift in the CI/CD pipeline, leading to a significant drop in sprint velocity. However, positive strides were made through the successful adoption of the design system and improved team morale following the introduction of asynchronous standups.',
      key_themes: ['Operational Efficiency', 'Documentation Gaps', 'Team Collaboration'],
      action_items: [
        'Automate API documentation generation by the end of Q2 2026.',
        'Implement infrastructure-as-code to ensure environment parity across staging and production by mid-Q2 2026.',
        'Schedule a cross-team sync meeting for roadmap alignment by April 2026.',
        'Conduct a post-implementation review of the new component library to measure dev time reduction by Q3 2026.',
        'Establish a continuous feedback loop for the documentation process with backend and frontend teams by May 2026.',
      ],
      sentiment_label: 'Positive',
      sentiment_score: 0.75,
    }),
    []
  );

  const scrollToGenerator = useCallback(() => {
    generatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const canGenerate = notes.trim().length >= 40 && pageState !== 'loading';

  const handleFillSample = useCallback(() => {
    if (pageState === 'loading' || isTyping) return;
    trackClick('HomePage_generator_fill_sample');
    setIsTyping(true);
    setIsUsingDemoData(true);
    setErrorMsg(null);
    setReport(null);
    setPageState('idle');
    setNotes('');

    let currentIndex = 0;
    const typeSpeed = 12;

    const typeNextChar = () => {
      if (currentIndex < sampleNotes.length) {
        setNotes(sampleNotes.slice(0, currentIndex + 1));
        currentIndex++;
        setTimeout(typeNextChar, typeSpeed);
      } else {
        setIsTyping(false);
      }
    };

    setTimeout(() => {
      scrollToGenerator();
      typeNextChar();
    }, 100);
  }, [pageState, isTyping, sampleNotes, scrollToGenerator]);

  const runAnalysisAnimation = useCallback(() => {
    const steps: AnalysisStep[] = [
      { id: 'sentiment', label: 'Analyzing Team Sentiment', status: 'pending' },
      { id: 'actions', label: 'Extracting Action Items', status: 'pending' },
      { id: 'themes', label: 'Identifying Key Themes', status: 'pending' },
      { id: 'velocity', label: 'Calculating Sprint Velocity', status: 'pending' },
      { id: 'summary', label: 'Generating Executive Summary', status: 'pending' },
    ];

    setAnalysisSteps(steps);
    setAnalysisProgress(0);

    let currentStep = 0;
    const stepDuration = 600;

    const progressInterval = setInterval(() => {
      setAnalysisProgress((prev) => Math.min(prev + 2, 100));
    }, 50);

    const advanceStep = () => {
      if (currentStep < steps.length) {
        setAnalysisSteps((prev) =>
          prev.map((s, i) => ({
            ...s,
            status: i < currentStep ? 'complete' : i === currentStep ? 'active' : 'pending',
          }))
        );
        currentStep++;
        if (currentStep <= steps.length) {
          setTimeout(advanceStep, stepDuration);
        }
      } else {
        clearInterval(progressInterval);
        setAnalysisProgress(100);
        setAnalysisSteps((prev) => prev.map((s) => ({ ...s, status: 'complete' })));
      }
    };

    setTimeout(advanceStep, 200);

    return () => clearInterval(progressInterval);
  }, []);

  const handleGenerate = async () => {
    if (!canGenerate) return;
    trackClick(isUsingDemoData ? 'HomePage_generator_generate_demo' : 'HomePage_generator_generate_real');
    setErrorMsg(null);
    setPageState('loading');
    setReport(null);

    runAnalysisAnimation();

    if (isUsingDemoData) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      setReport(mockDemoReport);
      setPageState('done');
      setAnalysisSteps([]);
      setTimeout(() => generatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      return;
    }

    const payload = {
      sprint_name: 'Sprint Demo',
      template: 'Freeform',
      date: new Date().toISOString().slice(0, 10),
      cards: `Notes: ${notes.trim()}`,
      action_items: '',
    };

    try {
      const res = await fetch(`${API_BASE}/public/ai-report/generate/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = (await res.json()) as GenerateResponse;

      if (!res.ok) {
        setErrorMsg(
          res.status === 429
            ? data.message || 'Daily limit reached. Create a free account for unlimited reports.'
            : data.message || 'Something went wrong. Please try again.',
        );
        setPageState('idle');
        setAnalysisSteps([]);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 2500));

      setReport(data.report || null);
      setPageState('done');
      setAnalysisSteps([]);
      setTimeout(() => generatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setPageState('idle');
      setAnalysisSteps([]);
    }
  };

  return (
    <div className="font-home-body min-h-screen overflow-x-hidden" style={{ background: '#fdfaf5', color: '#1d2b50' }}>

      {/* ── Announcement ── */}
      {showAnnouncement && (
        <div
          className="flex items-center justify-center gap-3 text-center px-4 py-2.5 text-[13.5px] font-medium"
          style={{ background: '#1d2b50', color: '#fff' }}
        >
          <span>
            ✦ New — AI‑powered retro summaries have landed.{' '}
            <Link to="/ai-retrospective-summary" style={{ color: '#ffb26b' }} className="underline">
              See it in action
            </Link>
          </span>
          <button
            type="button"
            aria-label="Dismiss announcement"
            onClick={() => setShowAnnouncement(false)}
            className="opacity-70 hover:opacity-100 transition-opacity"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Hero ── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-6 text-center">
        <div className="flex flex-wrap justify-center gap-2.5 font-display font-bold text-[17px] mb-6">
          <span style={{ background: '#ffe9d1', color: '#c95f08', transform: 'rotate(-2deg)' }} className="px-3.5 py-1.5 rounded-full inline-block">
            Retros.
          </span>
          <span style={{ background: '#e4f4ef', color: '#1e7a63', transform: 'rotate(1.5deg)' }} className="px-3.5 py-1.5 rounded-full inline-block">
            Voting.
          </span>
          <span style={{ background: '#ece7ff', color: '#5b46c4', transform: 'rotate(-1deg)' }} className="px-3.5 py-1.5 rounded-full inline-block">
            Actions.
          </span>
        </div>

        <h1 className="font-display font-extrabold text-[42px] sm:text-6xl lg:text-[60px] leading-[1.05] tracking-tight max-w-3xl mx-auto mb-5">
          AI‑powered retrospectives your team will actually enjoy
        </h1>

        <p className="text-[19px] leading-relaxed max-w-xl mx-auto mb-8" style={{ color: '#4d5875' }}>
          Retrosive is the easiest way to run agile retrospectives online. Real‑time collaboration, 12+
          templates, anonymous voting and action tracking — with an AI co‑facilitator that writes the
          summary so you don't have to.
        </p>

        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <GoogleAuthButton
              variant="dark"
              size="large"
              text="Sign up with Google"
              trackingEvent="HomePage_google_auth"
            />
            <Link
              to="/register"
              onClick={() => trackClick('HomePage_cta_get_started')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-white font-bold rounded-full text-[17px] whitespace-nowrap transition-colors"
              style={{ background: '#f27a1a', boxShadow: '0 3px 0 #c95f08' }}
            >
              Sign up for free
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <button
            type="button"
            onClick={() => {
              trackClick('HomePage_cta_try_generator');
              scrollToGenerator();
            }}
            className="text-sm underline underline-offset-2 transition-colors"
            style={{ color: '#6b7591' }}
          >
            or try the AI summary tool — no sign‑up needed
          </button>
          <div className="flex gap-5 text-[13.5px] font-medium" style={{ color: '#6b7591' }}>
            <span>✓ No credit card required</span>
            <span>✓ Free to start</span>
          </div>
        </div>
      </section>

      {/* ── Product mockup ── */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-2">
        <div
          className="rounded-[20px] p-5 relative"
          style={{ background: '#fff', border: '1px solid #e8e2d6', boxShadow: '0 18px 50px -20px rgba(29,43,80,.22)' }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#f4b8ad' }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#f5d78e' }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#a8d8b9' }} />
            <span className="ml-3 text-xs font-semibold" style={{ color: '#8b93a8' }}>
              Sprint 42 Retro · 8 people online
            </span>
            <span className="ml-auto flex">
              <span className="w-6.5 h-6.5 rounded-full border-2 border-white" style={{ width: 26, height: 26, background: '#ffd66b' }} />
              <span className="rounded-full border-2 border-white -ml-2" style={{ width: 26, height: 26, background: '#ff9eb5' }} />
              <span className="rounded-full border-2 border-white -ml-2" style={{ width: 26, height: 26, background: '#7fd8c9' }} />
              <span
                className="rounded-full border-2 border-white -ml-2 grid place-items-center text-[10px] font-bold"
                style={{ width: 26, height: 26, background: '#c8b6ff', color: '#4b3a90' }}
              >
                +5
              </span>
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl p-3.5 flex flex-col gap-2.5" style={{ background: '#f2f7f3' }}>
              <div className="font-bold text-[13.5px] flex items-center gap-1.5" style={{ color: '#1e7a63' }}>
                <span className="w-2 h-2 rounded-full" style={{ background: '#2fae8b' }} />
                Went well
              </div>
              <div className="animate-floaty rounded-lg p-3 text-[13px] font-semibold" style={{ background: '#ffd66b', boxShadow: '0 4px 10px rgba(0,0,0,.08)', transform: 'rotate(-1.5deg)' }}>
                Shipped the new onboarding two days early 🎉
              </div>
              <div className="rounded-lg p-3 text-[13px] font-semibold" style={{ background: '#ffd66b', boxShadow: '0 4px 10px rgba(0,0,0,.08)', transform: 'rotate(1deg)' }}>
                Pairing sessions on the API refactor
              </div>
              <div className="rounded-lg p-3 text-[13px] font-semibold" style={{ background: '#7fd8c9', boxShadow: '0 4px 10px rgba(0,0,0,.08)', transform: 'rotate(-0.5deg)' }}>
                QA turnaround was super fast
              </div>
            </div>

            <div className="rounded-2xl p-3.5 flex flex-col gap-2.5" style={{ background: '#fdf1f0' }}>
              <div className="font-bold text-[13.5px] flex items-center gap-1.5" style={{ color: '#c04a4a' }}>
                <span className="w-2 h-2 rounded-full" style={{ background: '#e0645f' }} />
                To improve
              </div>
              <div className="rounded-lg p-3 text-[13px] font-semibold" style={{ background: '#ff9eb5', boxShadow: '0 4px 10px rgba(0,0,0,.08)', transform: 'rotate(1.5deg)' }}>
                Too many meetings on Wednesdays
              </div>
              <div className="animate-floaty rounded-lg p-3 text-[13px] font-semibold" style={{ background: '#ff9eb5', boxShadow: '0 4px 10px rgba(0,0,0,.08)', transform: 'rotate(-1deg)' }}>
                Staging env keeps drifting from prod
              </div>
              <div
                className="flex items-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-semibold"
                style={{ background: '#fff', border: '1px dashed #e3b8b4', color: '#a06c69' }}
              >
                🗳 3 votes · anonymous
              </div>
            </div>

            <div className="rounded-2xl p-3.5 flex flex-col gap-2.5" style={{ background: '#f0f2fb' }}>
              <div className="font-bold text-[13.5px] flex items-center gap-1.5" style={{ color: '#4a58b8' }}>
                <span className="w-2 h-2 rounded-full" style={{ background: '#5b6fd6' }} />
                Actions
              </div>
              <div className="rounded-lg p-3 text-[13px] font-semibold" style={{ background: '#c8b6ff', boxShadow: '0 4px 10px rgba(0,0,0,.08)', transform: 'rotate(-1deg)' }}>
                Move standup to async on Wed
              </div>
              <div className="rounded-lg p-3 text-[13px] font-semibold" style={{ background: '#c8b6ff', boxShadow: '0 4px 10px rgba(0,0,0,.08)', transform: 'rotate(1deg)' }}>
                Automate staging refresh
              </div>
              <div className="rounded-[10px] p-3 text-xs leading-relaxed" style={{ background: '#fff', border: '1px solid #e2e0f5', color: '#4d5875' }}>
                <div className="flex items-center gap-1.5 font-bold mb-1" style={{ color: '#f27a1a', fontSize: 12 }}>
                  <span
                    className="w-4 h-4 rounded grid place-items-center text-white"
                    style={{ background: '#f27a1a', fontSize: 10 }}
                  >
                    ✦
                  </span>
                  AI Summary
                </div>
                Delivery pace is strong; meeting load and env drift are the top blockers. 2 actions assigned.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Logo cloud ── */}
      {/* <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-8 text-center">
        <p className="text-[13.5px] font-semibold mb-5" style={{ color: '#8b93a8', letterSpacing: '.04em' }}>
          Trusted by teams that take teamwork seriously
        </p>
        <div className="flex flex-wrap justify-center items-center gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <span
              key={i}
              className="grid place-items-center rounded-lg text-[10.5px]"
              style={{
                width: 128,
                height: 34,
                background: 'repeating-linear-gradient(45deg,#eee8dc,#eee8dc 6px,#f6f1e7 6px,#f6f1e7 12px)',
                fontFamily: 'ui-monospace, monospace',
                color: '#a89f8d',
              }}
            >
              customer logo
            </span>
          ))}
        </div>
      </section> */}

      {/* ── Features ── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-center font-bold text-sm mb-2.5" style={{ color: '#f27a1a', letterSpacing: '.05em', textTransform: 'uppercase' }}>
          Why Retrosive
        </p>
        <h2 className="font-display font-extrabold text-3xl sm:text-[40px] tracking-tight text-center max-w-xl mx-auto mb-3.5">
          Everything you need to run better retros
        </h2>
        <p className="text-center text-[17px] max-w-xl mx-auto mb-10 leading-relaxed" style={{ color: '#4d5875' }}>
          From the first sticky note to the follow‑up on last sprint's actions, Retrosive keeps the whole
          ceremony in one shared, structured space.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl p-6 transition-all hover:-translate-y-0.5"
              style={{ background: '#fff', border: '1px solid #e8e2d6' }}
            >
              <span
                className="grid place-items-center rounded-[11px] text-[17px] mb-3.5"
                style={{ width: 38, height: 38, background: f.bg }}
              >
                {f.icon}
              </span>
              <h3 className="font-display font-bold text-[19px] mb-2">{f.title}</h3>
              <p className="text-[14.5px] leading-relaxed" style={{ color: '#4d5875' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── AI co-facilitator ── */}
      <section className="my-6 py-16 px-4 sm:px-6" style={{ background: '#1d2b50', color: '#fff' }}>
        <div className="max-w-4xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="font-bold text-sm mb-2.5" style={{ color: '#ffb26b', letterSpacing: '.05em', textTransform: 'uppercase' }}>
              AI co‑facilitator
            </p>
            <h2 className="font-display font-extrabold text-[32px] sm:text-[38px] leading-tight tracking-tight mb-4">
              The retro writes its own minutes
            </h2>
            <p className="text-[16.5px] leading-relaxed mb-6" style={{ color: '#c3cbe2' }}>
              While your team talks, Retrosive listens to the board — grouping themes, spotting recurring
              blockers across sprints, and drafting a summary with suggested actions the moment the retro
              ends.
            </p>
            <div className="flex flex-col gap-2.5 text-[15px] font-semibold">
              {[
                'Instant theme grouping across columns',
                'Trends and recurring issues sprint over sprint',
                'Summary + action list, ready to paste into Slack',
              ].map((item) => (
                <span key={item} className="flex items-center gap-2.5">
                  <span style={{ color: '#ffb26b' }}>✦</span>
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-[18px] p-6" style={{ background: '#25355f', border: '1px solid #35476f' }}>
            <div className="flex items-center gap-2 font-bold text-sm mb-4">
              <span className="w-5.5 h-5.5 rounded-[7px] grid place-items-center text-xs" style={{ width: 22, height: 22, background: '#f27a1a' }}>✦</span>
              Sprint 42 — AI Summary
            </div>
            <div className="flex flex-col gap-2.5 text-[13.5px] leading-[1.55]" style={{ color: '#dbe2f2' }}>
              <div className="rounded-[10px] px-3.5 py-3" style={{ background: '#1d2b50' }}>
                <strong style={{ color: '#7fd8c9' }}>Top theme:</strong> delivery pace improved — onboarding shipped early, QA cycle down 30%.
              </div>
              <div className="rounded-[10px] px-3.5 py-3" style={{ background: '#1d2b50' }}>
                <strong style={{ color: '#ff9eb5' }}>Recurring blocker:</strong> staging drift, raised in 3 of the last 4 retros.
              </div>
              <div className="rounded-[10px] px-3.5 py-3" style={{ background: '#1d2b50' }}>
                <strong style={{ color: '#ffb26b' }}>Suggested action:</strong> automate staging refresh
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Summary Generator (functional) ── */}
      <section ref={generatorRef} className="py-16 px-4 sm:px-6 scroll-mt-16" style={{ background: '#ffe9d1' }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-start">

            {/* Left: context */}
            <div className="lg:col-span-2">
              <span
                className="inline-block px-4 py-1.5 rounded-full text-sm font-bold mb-6"
                style={{ background: '#fff', color: '#c95f08' }}
              >
                Try it free — no sign‑up needed
              </span>

              <h2 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight mb-4">
                Paste your notes.<br />
                Get a clear summary.
              </h2>

              <p className="leading-relaxed mb-8" style={{ color: '#4d5875' }}>
                Drop your sprint feedback in and see what your team actually needs to focus on next.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Spots patterns across all feedback',
                  'Pulls out clear action items',
                  'Flags team mood and blockers',
                  'Writes the summary for you',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm" style={{ color: '#4d5875' }}>
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#f27a1a' }} />
                    {item}
                  </div>
                ))}
              </div>

              <div className="p-5 rounded-xl" style={{ background: '#fff', border: '1px solid #f3d7ae' }}>
                <p className="text-sm mb-4" style={{ color: '#4d5875' }}>
                  Save history and sync with your team in one click.
                </p>
                <GoogleAuthButton
                  variant="light"
                  size="default"
                  text="Sign up with Google"
                  trackingEvent="HomePage_google_auth"
                />
              </div>
            </div>

            {/* Right: generator card */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e8e2d6', boxShadow: '0 12px 30px -14px rgba(29,43,80,.25)' }}>

                <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #f1ede2' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: '#ffe9d1' }}>
                      <Brain className="w-4 h-4" style={{ color: '#c95f08' }} />
                    </div>
                    <span className="font-bold">Retro Summary Generator</span>
                  </div>
                  <span className="text-xs font-medium" style={{ color: '#8b93a8' }}>Free · No login required</span>
                </div>

                <div className="p-6 space-y-4">
                  <div className="relative">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <button
                        type="button"
                        onClick={handleFillSample}
                        disabled={isTyping || pageState === 'loading'}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: '#f27a1a' }}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        {isTyping ? 'Typing...' : 'Use demo data'}
                      </button>
                    </div>

                    <div className="flex items-center justify-between mb-2 pt-5">
                      <label className="text-xs font-bold uppercase tracking-wider" style={{ color: '#8b93a8' }}>
                        Sprint Feedback
                      </label>
                      <span className="text-xs" style={{ color: notes.trim().length >= 40 ? '#f27a1a' : '#8b93a8' }}>
                        {notes.trim().length} / 40+ chars
                      </span>
                    </div>

                    <textarea
                      value={notes}
                      onChange={(e) => {
                        if (!isTyping) {
                          setNotes(e.target.value);
                          setIsUsingDemoData(false);
                        }
                      }}
                      rows={7}
                      placeholder="Paste your sprint retro notes here...&#10;&#10;Click 'Use demo data' above to see an example."
                      className="w-full rounded-xl px-4 py-3 text-sm resize-none transition-all focus:outline-none"
                      style={{ background: '#fdfaf5', border: '1px solid #e8e2d6', color: '#1d2b50' }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={!canGenerate}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-bold transition-colors"
                    style={
                      canGenerate
                        ? { background: '#f27a1a', color: '#fff' }
                        : { background: '#f1ede2', color: '#a89f8d', cursor: 'not-allowed' }
                    }
                  >
                    {pageState === 'loading' ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4" />
                        Generate Summary
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-center gap-2 text-xs" style={{ color: '#8b93a8' }}>
                    <Shield className="w-3.5 h-3.5" />
                    Your data is never used for AI training
                  </div>

                  {errorMsg && (
                    <div className="rounded-xl px-4 py-3 text-sm" style={{ border: '1px solid #f4b8ad', background: '#fdf1f0', color: '#c04a4a' }}>
                      {errorMsg}
                    </div>
                  )}

                  {pageState === 'loading' && analysisSteps.length > 0 && (
                    <div className="rounded-xl p-5 space-y-4" style={{ background: '#fdfaf5', border: '1px solid #f1ede2' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold flex items-center gap-2">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#ffb26b' }} />
                            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: '#f27a1a' }} />
                          </span>
                          Analyzing your retro
                        </span>
                        <span className="text-xs font-mono" style={{ color: '#f27a1a' }}>{analysisProgress}%</span>
                      </div>

                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#f1ede2' }}>
                        <div
                          className="h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${analysisProgress}%`, background: '#f27a1a' }}
                        />
                      </div>

                      <div className="space-y-2.5">
                        {analysisSteps.map((step) => (
                          <div
                            key={step.id}
                            className="flex items-center gap-3 text-sm transition-all duration-300"
                            style={{
                              color: step.status === 'complete' ? '#1e7a63' : step.status === 'active' ? '#1d2b50' : '#a89f8d',
                            }}
                          >
                            {step.status === 'complete' ? (
                              <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#2fae8b' }} />
                            ) : step.status === 'active' ? (
                              <span className="relative flex h-4 w-4 items-center justify-center flex-shrink-0">
                                <span className="animate-ping absolute h-3 w-3 rounded-full opacity-75" style={{ background: '#ffb26b' }} />
                                <span className="relative h-2 w-2 rounded-full" style={{ background: '#f27a1a' }} />
                              </span>
                            ) : (
                              <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ border: '1px solid #d8d1c2' }} />
                            )}
                            <span className={step.status === 'active' ? 'font-semibold' : ''}>{step.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pageState !== 'loading' && (
                    <div className="grid md:grid-cols-3 gap-3">
                      <div className="rounded-xl p-4" style={{ background: '#fdfaf5', border: '1px solid #f1ede2' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-3.5 h-3.5" style={{ color: '#f27a1a' }} />
                          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#8b93a8' }}>Summary</span>
                        </div>
                        <p className="text-xs leading-relaxed min-h-[64px]" style={{ color: '#4d5875' }}>
                          {report?.executive_summary || 'Your executive summary will appear here after generation.'}
                        </p>
                      </div>

                      <div className="rounded-xl p-4" style={{ background: '#fdfaf5', border: '1px solid #f1ede2' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-3.5 h-3.5" style={{ color: '#f27a1a' }} />
                          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#8b93a8' }}>Themes</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 min-h-[64px]">
                          {(report?.key_themes && report.key_themes.length > 0
                            ? report.key_themes.slice(0, 4)
                            : ['CI/CD', 'Velocity', 'Docs', 'Design']
                          ).map((t) => (
                            <span key={t} className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#ffe9d1', color: '#c95f08' }}>
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-xl p-4" style={{ background: '#fdfaf5', border: '1px solid #f1ede2' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <ListChecks className="w-3.5 h-3.5" style={{ color: '#f27a1a' }} />
                          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#8b93a8' }}>Actions</span>
                        </div>
                        <ul className="space-y-2 min-h-[64px]">
                          {(report?.action_items && report.action_items.length > 0
                            ? report.action_items.slice(0, 3)
                            : ['Fix CI/CD blockers', 'Automate docs', 'Address config drift']
                          ).map((a) => (
                            <li key={a} className="flex items-start gap-1.5 text-xs" style={{ color: '#4d5875' }}>
                              <ArrowRight className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: '#f5a86a' }} />
                              <span>{a}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {report?.sentiment_label && (
                    <div className="flex items-center gap-2 text-sm pt-1" style={{ color: '#4d5875' }}>
                      <Gauge className="w-4 h-4" style={{ color: '#f5a86a' }} />
                      Team Sentiment:{' '}
                      <span className="font-bold" style={{ color: '#1d2b50' }}>{report.sentiment_label}</span>
                      {report.sentiment_score != null && (
                        <span style={{ color: '#a89f8d' }}>({Math.round(report.sentiment_score * 100)}%)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Templates ── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-display font-extrabold text-3xl sm:text-[38px] tracking-tight text-center max-w-xl mx-auto mb-3.5">
          Start with a proven format
        </h2>
        <p className="text-center text-[16.5px] max-w-xl mx-auto mb-10 leading-relaxed" style={{ color: '#4d5875' }}>
          Ready‑made templates for every team and retrospective style.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {templates.map((t) => (
            <Link
              key={t.slug}
              to={`/templates/${t.slug}`}
              className="group rounded-2xl p-5 text-center transition-all"
              style={{ background: '#fff', border: '1px solid #e8e2d6' }}
            >
              <div className="text-3xl mb-2.5">{t.emoji}</div>
              <h3 className="font-display font-bold text-base mb-1.5">{t.name}</h3>
              <p className="text-[13px] leading-relaxed" style={{ color: '#4d5875' }}>{t.desc}</p>
            </Link>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/templates" className="inline-flex items-center gap-2 font-bold transition-colors" style={{ color: '#f27a1a' }}>
            Browse all templates <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── Testimonial ── */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-center">
        <div className="font-display leading-none mb-1.5" style={{ fontSize: 70, color: '#f27a1a' }}>&ldquo;</div>
        <p className="font-display font-bold text-2xl sm:text-[26px] leading-[1.35] tracking-tight mb-5">
          Retrosive turned our retros from a chore into the meeting people actually show up early for.
          The AI summary alone saves me an hour every sprint.
        </p>
        <div className="flex items-center justify-center gap-3">
          <span
            className="rounded-full grid place-items-center text-[8px]"
            style={{
              width: 44,
              height: 44,
              background: 'repeating-linear-gradient(45deg,#eee8dc,#eee8dc 5px,#f6f1e7 5px,#f6f1e7 10px)',
              fontFamily: 'ui-monospace, monospace',
              color: '#a89f8d',
            }}
          >
            photo
          </span>
          <div className="text-left text-sm">
            <strong>Engineering Manager</strong>
            <br />
            <span style={{ color: '#6b7591' }}>placeholder — real quote goes here</span>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-16">
        <h2 className="font-display font-extrabold text-[34px] tracking-tight text-center mb-8">
          Would you like to know more?
        </h2>
        <div className="flex flex-col gap-2.5">
          {faqs.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={item.q} className="rounded-2xl px-5 sm:px-[22px] py-4" style={{ background: '#fff', border: '1px solid #e8e2d6' }}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left font-bold text-base"
                >
                  {item.q}
                  <span className="text-xl font-normal flex-shrink-0" style={{ color: '#f27a1a' }}>{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && (
                  <p className="text-[14.5px] leading-relaxed mt-3" style={{ color: '#4d5875' }}>{item.a}</p>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-sm mt-6" style={{ color: '#6b7591' }}>
          Still have a question? <a href="mailto:support@retrosive.com" style={{ color: '#f27a1a' }}>Contact us</a> — we'll be happy
          to help. Comparing tools? <Link to="/alternatives" style={{ color: '#f27a1a' }}>See how Retrosive stacks up</Link>.
        </p>
      </section>

      {/* ── CTA ── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div
          className="rounded-3xl px-6 sm:px-10 py-14 text-center text-white"
          style={{ background: 'linear-gradient(120deg,#f27a1a,#e8560f)' }}
        >
          <h2 className="font-display font-extrabold text-[32px] sm:text-4xl tracking-tight max-w-xl mx-auto mb-3.5">
            Ready for retros your team looks forward to?
          </h2>
          <p className="text-[17px] max-w-md mx-auto mb-7" style={{ opacity: 0.92 }}>
            Retrosive is free to start — set up your first board in under a minute.
          </p>
          <Link
            to="/register"
            onClick={() => trackClick('HomePage_cta_bottom')}
            className="inline-block font-extrabold text-[17px] px-8 py-4 rounded-full transition-transform hover:-translate-y-0.5"
            style={{ background: '#fff', color: '#e8560f', boxShadow: '0 3px 0 rgba(0,0,0,.18)' }}
          >
            Sign up for free
          </Link>
          <div className="flex justify-center gap-5 text-[13.5px] font-semibold mt-4" style={{ opacity: 0.9 }}>
            <span>✓ No credit card required</span>
            <span>✓ Endless good vibes</span>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default HomePage;
